import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  AvailabilityState,
  CatalogVariantFragmentFragment,
  Roles,
} from '@notino/shared/definitions/types';

import { ILocale } from '@containers/App/model';
import { IPrice } from '@containers/ProductListing/model';
import {
  IDamage,
  MaximumVolumeInPercent,
} from '@containers/ProductListing/model';
import { useUser } from '@queries/useUser';

import messages from '../../../messages';
import { useIsInternalTraffic } from '../../hooks/useIsInternalTraffic';
import { usePriceLogic } from '../../hooks/usePriceLogic';
import { AdditionalDescriptionStyled } from '../AdditionalText/styled';
import { PriceRatio } from '../VariantsInSelectBox/PriceRatio';

import { DisplayPrice } from './DisplayPrice';
import {
  Wrapper,
  CodeBlock,
  CodeName,
  Separator,
  Availability,
} from './styled';
import { useIsMinPriceDisplayedInVoucher } from './useIsMinPriceDisplayedInVoucher';

const Damage: React.FC<{ damage: IDamage }> = ({ damage }) => {
  const message =
    damage.volumeInPercent === MaximumVolumeInPercent
      ? messages.productDamaged
      : messages.productReturned;

  return (
    <AdditionalDescriptionStyled highlight={true}>
      <FormattedMessage {...message} />
    </AdditionalDescriptionStyled>
  );
};

interface IBelowTheLineProps {
  variant: CatalogVariantFragmentFragment;
  locale: ILocale;
}

export const BellowTheLine: React.FC<IBelowTheLineProps> = React.memo(
  ({ variant, locale }) => {
    const {
      attributes,
      orderCode,
      productCode,
      eanCode,
      webId,
      catalogId,
      availability,
      stockAvailability,
    } = variant;

    const { formatDate } = useIntl();
    const { user } = useUser();
    const { bottomRecentPrice } = usePriceLogic(variant);
    const isInternalTraffic = useIsInternalTraffic();

    const isMinPriceDisplayedInVoucher =
      useIsMinPriceDisplayedInVoucher(variant);

    const isImmediateCollection =
      availability.availablePickUpStores > 0 &&
      availability.state !== AvailabilityState.CanBeBought;

    const isMaster = attributes && attributes.Master;

    const getAvailabilityMessage = () => {
      if (isImmediateCollection) {
        return <FormattedMessage {...messages.immediateCollection} />;
      }

      if (stockAvailability.code === IStock.outOfStock) {
        if (availability.state === AvailabilityState.Disabled) {
          return <FormattedMessage {...messages.disabled} />;
        }

        if (availability.state === AvailabilityState.ShowWatchdog) {
          return <FormattedMessage {...messages.outOfStock} />;
        }
      }

      return (
        <FormattedMessage
          {...messages[stockAvailability.code ?? '']}
          values={{
            count: stockAvailability.count,
          }}
        />
      );
    };

    return (
      <Wrapper data-testid="product-specifications">
        <Availability
          highlight={[IStock.outOfStock, IStock.lessThan5].includes(
            stockAvailability.code as IStock
          )}
        >
          {getAvailabilityMessage()}
        </Availability>

        {variant.unitPrice && (
          <PriceRatio
            prepend={<Separator> | </Separator>}
            stockAvailability={variant.stockAvailability.code ?? ''}
            unitAmount={variant.unitPrice.perAmount ?? 0}
            locale={locale}
            unit={variant.unitPrice.unit ?? ''}
            unitPrice={variant.unitPrice as any}
          />
        )}

        {bottomRecentPrice && !isMinPriceDisplayedInVoucher && (
          <DisplayPrice
            priceValue={bottomRecentPrice.value}
            label={messages.minimumRecentPrice}
            afterPriceText={undefined}
            tooltip={bottomRecentPrice.tooltip}
          />
        )}

        {variant.anchorPrice?.validFrom && (
          <DisplayPrice
            priceValue={variant.anchorPrice as IPrice}
            label={messages.anchorPrice}
            labelValues={{
              date: formatDate(variant.anchorPrice.validFrom, {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
              }),
            }}
          />
        )}

        <Separator> | </Separator>

        <CodeBlock>
          <CodeName>
            <FormattedMessage {...messages.code} />{' '}
          </CodeName>

          {user?.role === Roles.Admin || isInternalTraffic ? (
            <span>
              <span role="button">{orderCode}</span> <b>{productCode}</b>{' '}
              {webId} {catalogId}
              {isMaster && <b> M</b>}
            </span>
          ) : (
            orderCode
          )}
        </CodeBlock>

        {(user?.role === Roles.Admin || isInternalTraffic) && eanCode && (
          <AdditionalDescriptionStyled>
            EAN: {eanCode}
          </AdditionalDescriptionStyled>
        )}

        {attributes.IsForProfessionals && (
          <AdditionalDescriptionStyled highlight={true}>
            <FormattedMessage {...messages.isForProfessionals} />
          </AdditionalDescriptionStyled>
        )}

        {attributes.Damage && <Damage damage={attributes.Damage} />}
      </Wrapper>
    );
  }
);
